import axios from 'axios'

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1'

const getAuthHeaders = token => ({
  headers: { Authorization: `Bearer ${token}` },
})

/**
 * Fetches all available Arcanos.
 * @param {string} token - The authentication token.
 * @returns {Promise<Array>} - A promise that resolves to an array of Arcanos.
 */
export const getArcanos = async token => {
  try {
    const response = await axios.get(`${API_BASE_URL}/arcanas/`, getAuthHeaders(token))
    return response.data
  } catch (error) {
    console.error('Error fetching arcanos:', error)
    console.error('Error details:', error.response?.data)
    console.error('Error status:', error.response?.status)
    console.error('Request headers sent:', error.config?.headers)
    throw error
  }
}

/**
 * Creates a new Arcano (admin only).
 * @param {string} token - The authentication token.
 * @param {object} arcanoData - The Arcano data.
 * @param {string} arcanoData.name - The name of the Arcano.
 * @param {string} arcanoData.arcana_id - The unique identifier for the Arcano.
 * @param {string} [arcanoData.description] - The description of the Arcano.
 * @param {string} [arcanoData.system_prompt_override] - Custom system prompt for the Arcano.
 * @param {Array<string>} [arcanoData.allowed_teams] - List of teams allowed to access this Arcano.
 * @returns {Promise<object>} - A promise that resolves to the created Arcano.
 */
export const createArcano = async (token, arcanoData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/arcanas/`,
      arcanoData,
      getAuthHeaders(token)
    )
    return response.data
  } catch (error) {
    console.error('Error creating arcano:', error)
    throw error
  }
}

/**
 * Updates an existing Arcano (admin only).
 * @param {string} token - The authentication token.
 * @param {string} arcanaId - The ID of the Arcano to update.
 * @param {object} arcanoData - The Arcano data to update.
 * @param {string} [arcanoData.name] - The name of the Arcano.
 * @param {string} [arcanoData.description] - The description of the Arcano.
 * @param {string} [arcanoData.system_prompt_override] - Custom system prompt for the Arcano.
 * @param {Array<string>|null} [arcanoData.allowed_teams] - List of teams allowed to access this Arcano, or null for public access.
 * @returns {Promise<object>} - A promise that resolves to the updated Arcano.
 */
export const updateArcano = async (token, arcanaId, arcanoData) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/arcanas/${arcanaId}`,
      arcanoData,
      getAuthHeaders(token)
    )
    return response.data
  } catch (error) {
    console.error('Error updating arcano:', error)
    throw error
  }
}

/**
 * Deletes an Arcano (admin only).
 * @param {string} token - The authentication token.
 * @param {string} arcanaId - The ID of the Arcano to delete.
 * @returns {Promise<object>} - A promise that resolves to the deletion confirmation.
 */
export const deleteArcano = async (token, arcanaId) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/arcanas/${arcanaId}`,
      getAuthHeaders(token)
    )
    return response.data
  } catch (error) {
    console.error('Error deleting arcano:', error)
    throw error
  }
}

/**
 * Clears all knowledge associated with an Arcano (admin only).
 * @param {string} token - The authentication token.
 * @param {string} arcanaId - The ID of the Arcano whose knowledge to clear.
 * @returns {Promise<object>} - A promise that resolves to the deletion confirmation.
 */
export const clearArcanoKnowledge = async (token, arcanaId) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/arcanas/${arcanaId}/knowledge`,
      getAuthHeaders(token)
    )
    return response.data
  } catch (error) {
    console.error('Error clearing arcano knowledge:', error)
    throw error
  }
}
